/**
 * Nachi Bearing Finder for Apollo Industries, ported from the NSK page's finder.
 *
 * Finds Nachi bearings by part number (any brand's number is translated to Nachi), by size, or by bearing type,
 * using the compact index in nachi-finder-index.json (tools/build-nachi-index.mjs). Prices and stock on the result
 * rows are refreshed live from /products/{handle}.js.
 *
 * It lives inside the Nachi hero (sections/nachi-hero.liquid): the hero's example part number card becomes the
 * finder, and the hero's own script keeps handling the card's dimension rows (refreshTitleBlock rebinds them after
 * the finder redraws them). Pure helpers are exposed on globalThis.NachiFinderCore for tools/test-nachi-finder.mjs.
 */
(() => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Designation helpers                                                 */
  /* ------------------------------------------------------------------ */

  const BRAND_WORDS = /\b(NACHI|FUJIKOSHI|NSK|SKF|FAG|INA|NTN|KOYO|JTEKT|TIMKEN|SCHAEFFLER|BEARINGS?)\b/g;

  /**
   * Other brands' seal and shield codes, longest first, mapped to the Nachi code (NACHI_FACTS section 3:
   * 2NSE = SKF 2RS1 / 2RSH = NSK DDU = NTN LLU = FAG 2RSR; 2NKE = SKF 2RZ = NSK VV = NTN LLB; ZZE = 2Z).
   */
  const SEAL_MAP = [
    ['2RSH', '2NSE'], ['2RS1', '2NSE'], ['2RSR', '2NSE'], ['2HRS', '2NSE'], ['2RSL', '2NSE'], ['2RS', '2NSE'],
    ['DDU', '2NSE'], ['LLU', '2NSE'], ['LLH', '2NSE'],
    ['2BRS', '2NKE'], ['LLB', '2NKE'], ['2RZ', '2NKE'], ['2RU', '2NKE'], ['VV', '2NKE'],
    ['2ZR', 'ZZE'], ['2Z', 'ZZE'],
    ['RSH', 'NSE'], ['RS1', 'NSE'], ['RSR', 'NSE'], ['RS', 'NSE'], ['DU', 'NSE'], ['LU', 'NSE'],
    ['LB', 'NKE'], ['RZ', 'NKE'], ['RU', 'NKE'], ['ZR', 'ZE'],
  ];

  const PREFIXES = {
    NU: 'Cylindrical roller, two ribs on the outer ring and none on the inner',
    NJ: 'Cylindrical roller, two ribs on the outer ring and one on the inner',
    NUP: 'Cylindrical roller, NJ design plus a loose rib ring',
    N: 'Cylindrical roller, two ribs on the inner ring and none on the outer',
    NF: 'Cylindrical roller, two ribs on the inner ring and one on the outer',
    NH: 'Cylindrical roller, NJ design with an L-shaped thrust collar',
    NUH: 'Cylindrical roller, NU design with an L-shaped thrust collar',
    NP: 'Cylindrical roller, NF design plus a loose rib ring',
    NN: 'Double-row cylindrical roller',
    NNU: 'Double-row cylindrical roller, ribs on the outer ring',
    RNU: 'Cylindrical roller, NU design without an inner ring',
  };

  const HOUSINGS = {
    '': 'insert bearing only',
    P: 'in a pillow block housing',
    F: 'in a 4-bolt square flange housing',
    FL: 'in a 2-bolt oval flange housing',
    FC: 'in a round piloted flange housing',
    T: 'in a take-up housing',
  };

  const DIAMETER_SERIES = {
    0: 'Extra light series', 1: 'Extra light series', 2: 'Light series', 3: 'Medium series',
    4: 'Heavy series', 8: 'Thin section series', 9: 'Thin section series',
  };

  /** Seal and shield codes, shared by the ball bearing tables. The "9" is read as part of the seal code. */
  const SEALS = [
    { re: /^2NS(E9?|L)?/, group: 'seal', label: 'Contact rubber seals on both sides' },
    { re: /^NS(E9?|L)?/, group: 'seal', label: 'Contact rubber seal on one side' },
    { re: /^2NK(E9?)?/, group: 'seal', label: 'Non-contact rubber seals on both sides' },
    { re: /^NK(E9?)?/, group: 'seal', label: 'Non-contact rubber seal on one side' },
    { re: /^ZZE?/, group: 'seal', label: 'Steel shields on both sides' },
    { re: /^ZE?/, group: 'seal', label: 'Steel shield on one side' },
  ];

  /** Suffix codes per family (Nachi Tables 4.6.3 to 4.6.11). `after` means the token is only read once that group has been read. */
  const SUFFIXES = {
    ball: [
      ...SEALS,
      { re: /^NR/, group: 'ring', label: 'Snap ring groove with the snap ring fitted' },
      { re: /^N(?![SK])/, group: 'ring', label: 'Snap ring groove in the outer ring' },
      { re: /^K30/, group: 'taper', label: 'Tapered bore, 1:30' },
      { re: /^K/, group: 'taper', label: 'Tapered bore, 1:12' },
      { re: /^G/, group: 'cage', label: 'Polyamide (nylon) cage' },
      { re: /^MY?/, group: 'cage', label: 'Machined brass cage' },
      { re: /^CM/, group: 'clearance', label: 'Electric motor clearance' },
    ],
    angular: [
      { re: /^AC/, group: 'angle', label: '25° contact angle' },
      { re: /^A/, group: 'angle', label: '30° contact angle' },
      { re: /^B/, group: 'angle', label: '40° contact angle' },
      { re: /^C(?![2-5MN])/, group: 'angle', label: '15° contact angle' },
      ...SEALS,
      { re: /^Y/, group: 'cage', label: 'Polyamide resin cage' },
      { re: /^G(?![LNMH])/, group: 'cage', label: 'Polyamide (nylon) cage' },
      { re: /^MY?/, group: 'cage', label: 'Machined brass cage' },
      { re: /^DU/, group: 'arrangement', label: 'Universal flush ground, matched pair' },
      { re: /^DB/, group: 'arrangement', label: 'Matched pair, back-to-back' },
      { re: /^DF/, group: 'arrangement', label: 'Matched pair, face-to-face' },
      { re: /^DT/, group: 'arrangement', label: 'Matched pair, tandem' },
      { re: /^U/, group: 'arrangement', label: 'Universal flush ground, single bearing' },
      { re: /^GL/, group: 'preload', label: 'Light preload' },
      { re: /^GN/, group: 'preload', label: 'Normal preload' },
      { re: /^GM/, group: 'preload', label: 'Medium preload' },
      { re: /^GH/, group: 'preload', label: 'Heavy preload' },
    ],
    cylindrical: [
      { re: /^EG/, group: 'design', label: 'High-capacity design with a polyamide (nylon) cage' },
      { re: /^E/, group: 'design', label: 'High-capacity design' },
      { re: /^G/, group: 'cage', label: 'Polyamide (nylon) cage' },
      { re: /^MY?/, group: 'cage', label: 'Machined brass cage' },
      { re: /^K/, group: 'taper', label: 'Tapered bore, 1:12' },
    ],
    spherical: [
      { re: /^EXQ/, group: 'design', label: 'EXQ high-capacity design, heat stabilized for minimal size change up to 200 °C' },
      { re: /^EXS1/, group: 'design', label: 'EXS1 high-capacity thrust design' },
      { re: /^EX/, group: 'design', label: 'EX high-capacity design' },
      { re: /^E(?![1-4])/, group: 'design', label: 'High-capacity design' },
      { re: /^W33/, group: 'lube', label: 'Lubrication groove and holes in the outer ring' },
      { re: /^W20/, group: 'lube', label: 'Lubrication holes in the outer ring' },
      { re: /^K30/, group: 'taper', label: 'Tapered bore, 1:30' },
      { re: /^K/, group: 'taper', label: 'Tapered bore, 1:12' },
      { re: /^V/, group: 'vibration', label: 'Design for vibrating machinery such as screens' },
      { re: /^MY?/, group: 'cage', label: 'Machined brass cage' },
    ],
    tapered: [{ re: /^J/, group: 'design', label: 'Cup and cone interchangeable with other ISO metric brands' }],
  };

  const COMMON_SUFFIXES = [
    { re: /^C2/, group: 'clearance', label: 'Clearance smaller than normal' },
    { re: /^CN/, group: 'clearance', label: 'Normal clearance' },
    { re: /^C3/, group: 'clearance', label: 'Clearance greater than normal, the U.S. stock standard' },
    { re: /^C4/, group: 'clearance', label: 'Clearance greater than C3' },
    { re: /^C5/, group: 'clearance', label: 'Clearance greater than C4' },
    { re: /^P[2456]/, group: 'precision', label: 'Precision class' },
  ];

  const ABEC = { 6: 3, 5: 5, 4: 7, 2: 9 };

  /**
   * @param {string} code two-digit bore code
   * @returns {number} bore in millimetres
   */
  function boreFromCode(code) {
    const n = Number(code);
    return [10, 12, 15, 17][n] ?? n * 5;
  }

  /**
   * Best guess at the bearing family from the leading letters and basic number.
   * @param {string} prefix
   * @param {string} number
   * @returns {string|null}
   */
  function guessFamily(prefix, number) {
    if (/^(NU|NJ|NUP|N|NF|NH|NUH|NP|NN|NNU|RNU)$/.test(prefix)) return 'cylindrical';
    if (/^U[CK]/.test(prefix)) return 'mounted';
    if (prefix) return null;

    const series = seriesOf(number);
    if (/^(6\d?|16[01]|61[89])$/.test(series)) return 'deep-groove';
    if (/^4[23]$/.test(series)) return 'deep-groove';
    if (/^7\d{0,2}$/.test(series)) return 'angular';
    if (/^[35][23]$/.test(series)) return 'angular';
    if (/^[12][0-3]$/.test(series)) return 'self-aligning';
    if (/^2(13|22|23|30|31|32|38|39|40|41|48|49)$/.test(series)) return 'spherical';
    if (/^3(02|03|13|20|22|23|29|30|31|32)$/.test(series)) return 'tapered';
    if (/^(5(11|12|13|14|22|23|24)|29[234])$/.test(series)) return 'thrust';
    return null;
  }

  /** @param {string} number */
  function seriesOf(number) {
    if (number.includes('/')) return number.split('/')[0];
    if (number.length === 3) return number.slice(0, 2);
    return number.slice(0, -2);
  }

  /**
   * One spelling for codes Nachi writes several ways, so matching ignores the difference: the seal codes 2NSE9,
   * 2NSE, 2NSL and 2NS (the 9 is not used consistently) become 2NS, 2NKE9 becomes 2NK, ZZE becomes ZZ and ZE becomes Z.
   * @param {string} suffix
   */
  function canonical(suffix) {
    return suffix
      .replace(/(2?)N([SK])(?:E9?|L)?/g, '$1N$2')
      .replace(/ZZE/g, 'ZZ')
      .replace(/ZE/g, 'Z');
  }

  /**
   * Normalises any brand's designation into Nachi form.
   * "SKF 6205-2RSH/C3" becomes { display: "6205-2NSE9 C3" parts, key: "62052NSC3", notes: [["2RSH","2NSE9"]] }.
   * @param {string} raw
   */
  function translate(raw) {
    const cleaned = String(raw || '')
      .toUpperCase()
      .replace(/\([^)]*\)/g, ' ')
      .replace(/=\s*\d+\s*PCS\.?/g, ' ')
      .replace(BRAND_WORDS, ' ')
      .replace(/^[\s-]+/, '')
      .trim();
    const notes = [];
    const unit = cleaned.match(/^(U[CK][A-Z]*)\s*-?\s*(\d{3})\s*-\s*(\d{3})(.*)$/);
    const screw = !unit && cleaned.match(/^(\d{2,3})\s*(TA[A-Z])\s*(\d{2})(.*)$/);
    const match = unit || screw || cleaned.match(/^([A-Z]{0,4})[\s-]*(\d{2,6}(?:\/\d{1,4})?)(.*)$/);
    if (!match) {
      const key = cleaned.replace(/[\s\-/.]+/g, '');
      return { key, base: key, prefix: '', number: '', inch: '', suffix: '', family: null, screw: null, notes };
    }
    if (screw) {
      // Ball screw support bearings lead with the bore: 17TAU06F is a 17 mm bore TAU bearing, size 06, design F.
      const suffix = screw[4].replace(/[\s\-/.]+/g, '');
      const base = `${screw[1]}${screw[2]}${screw[3]}`;
      return { key: base + canonical(suffix), base, prefix: screw[2], number: screw[3], inch: '', suffix, family: 'thrust', screw: { bore: screw[1], series: screw[2], size: screw[3] }, notes };
    }
    let prefix = match[1];
    let number = match[2];
    const inch = unit ? match[3] : '';
    let suffix = (unit ? match[4] : match[3]).replace(/[\s\-/.]+/g, '');
    // SKF and FAG number double-row angular contact bearings 3200 / 3300; Nachi, NSK and NTN use 5200 / 5300.
    if (!prefix && /^3[23]\d\d$/.test(number)) {
      notes.push([number, `5${number.slice(1)}`]);
      number = `5${number.slice(1)}`;
    }
    const family = guessFamily(prefix, number);

    if (family === 'deep-groove' || family === 'self-aligning' || family === 'angular') {
      // the seal can follow a contact angle or design letter (SKF 3207 A-2RS1, 2205 E-2RS1)
      const lead = family === 'deep-groove' ? '' : (suffix.match(/^(AC|A|B|C|E)(?=[2LDV])/) || [''])[0];
      const rest = suffix.slice(lead.length);
      // angular contact bearings only take the two-sided codes; their DU is a matched pair, not a seal
      const swap = SEAL_MAP.find(([from]) => rest.startsWith(from) && (family !== 'angular' || /^(2|LL|DDU|VV)/.test(from)));
      if (swap) {
        let to = swap[1];
        if (family === 'angular') to = to.replace(/^(2?N[SK])E$/, '$1');
        else if (/E$/.test(to) && /N[SK]/.test(to) && boreOfNumber(number) >= 10 && boreOfNumber(number) <= 50) to += '9';
        notes.push([swap[0], to]);
        suffix = lead + to + rest.slice(swap[0].length);
      }
    }
    // NSK marks the lubrication groove and holes E4 (22215EAE4); Nachi, like SKF and FAG, writes W33
    if (family === 'spherical' && /E4/.test(suffix) && !/W33/.test(suffix)) {
      notes.push(['E4', 'W33']);
      suffix = suffix.replace('E4', 'W33');
    }
    const base = prefix + number + (inch ? `-${inch}` : '');
    return { key: base + canonical(suffix), base, prefix, number, inch, suffix, family, screw: null, notes };
  }

  /** Bore of a plain basic number, for the seal "9" rule: 6205 is 25 mm, 608 is 8 mm. */
  function boreOfNumber(number) {
    if (number.includes('/')) return Number(number.split('/')[1]);
    if (number.length === 3) return Number(number[2]);
    return boreFromCode(number.slice(-2));
  }

  /**
   * Explains each part of a Nachi designation.
   * @param {string} raw
   * @param {string|null} [knownFamily] family from the catalogue, when the part is stocked
   * @returns {{ key: string, family: string|null, bore: number|null, segments: Array<{code: string, label: string, kind: string}>, notes: string[][] }}
   */
  function decode(raw, knownFamily = null) {
    const t = translate(raw);
    const family = knownFamily || t.family;
    const segments = [];
    let bore = null;
    if (!t.number) return { key: t.key, family, bore, segments, notes: t.notes };

    if (t.screw) {
      bore = Number(t.screw.bore);
      segments.push({ code: t.screw.bore, label: `${bore} mm bore`, kind: 'bore' });
      segments.push({ code: t.screw.series, label: `Ball screw support bearing, ${t.screw.series} series`, kind: 'type' });
      segments.push({ code: t.screw.size, label: 'Size code within the series', kind: 'size' });
    } else if (t.prefix) {
      if (/^U[CK]/.test(t.prefix)) {
        const housing = HOUSINGS[t.prefix.slice(2)] ?? 'with a housing';
        const insert = t.prefix.startsWith('UK') ? 'Insert bearing for an adapter sleeve' : 'Insert bearing with set screws';
        segments.push({ code: t.prefix, label: `${insert}, ${housing}`, kind: 'type' });
      } else {
        segments.push({ code: t.prefix, label: PREFIXES[t.prefix] || 'Series prefix', kind: 'type' });
      }
    }

    if (t.screw) {
      // read above
    } else if (!t.prefix && /^52[234]\d\d$/.test(t.number)) {
      // Double-direction thrust bearings: the size code belongs to the housing washers, the shaft bore is smaller.
      pushSeries(segments, t.number.slice(0, 3), family, false);
      segments.push({ code: t.number.slice(3), label: 'Size code, the shaft washer bore is smaller', kind: 'bore' });
    } else if (t.number.includes('/')) {
      const [series, size] = t.number.split('/');
      pushSeries(segments, series, family, Boolean(t.prefix));
      segments.push({ code: `/${size}`, label: `${Number(size)} mm bore`, kind: 'bore' });
      bore = Number(size);
    } else if (t.number.length === 3 && !t.prefix) {
      pushSeries(segments, t.number.slice(0, 2), family, false);
      bore = Number(t.number[2]);
      segments.push({ code: t.number[2], label: `${bore} mm bore`, kind: 'bore' });
    } else if (t.number.length >= 3) {
      const code = t.number.slice(-2);
      pushSeries(segments, t.number.slice(0, -2), family, Boolean(t.prefix));
      bore = boreFromCode(code);
      let label = t.prefix.startsWith('UK') ? `${bore} mm tapered bore, fits a smaller shaft with an adapter sleeve` : `${bore} mm bore`;
      if (t.prefix === 'RNU') {
        // no inner ring: the rollers run on the shaft, so the size is the roller diameter in the title, not code x 5
        label = 'Size code of the matching NU bearing; the rollers run directly on the shaft';
        bore = null;
      }
      segments.push({ code, label, kind: 'bore' });
    }

    if (t.inch) {
      const inches = Number(t.inch[0]) + Number(t.inch.slice(1)) / 16;
      segments.push({ code: `-${t.inch}`, label: `${formatInches(inches)} shaft bore`, kind: 'bore' });
    }

    // ball screw support bearings carry angular contact codes (seals, GM preload) after the size
    const table = [...(SUFFIXES[t.screw ? 'angular' : suffixTableFor(family, t.number)] || []), ...COMMON_SUFFIXES];
    const read = new Set();
    let rest = t.suffix;
    let unknown = '';
    const flushUnknown = () => {
      if (!unknown) return;
      segments.push({ code: unknown, label: 'Special design, material or packaging code', kind: 'other' });
      unknown = '';
    };
    while (rest) {
      const token = table.find((entry) => !read.has(entry.group) && (!entry.after || read.has(entry.after)) && entry.re.test(rest));
      if (!token) {
        unknown += rest[0];
        rest = rest.slice(1);
        continue;
      }
      flushUnknown();
      const code = rest.match(token.re)[0];
      read.add(token.group);
      let label = token.label;
      if (token.group === 'precision') label = `ISO precision class ${code[1]} (ABEC ${ABEC[code[1]]})`;
      if (token.group === 'seal' && code.endsWith('9')) label += '; the 9 is used on 10 to 50 mm bores';
      // Nachi writes rubber seal codes after a hyphen (6205-2NSE9); shields follow the number directly (6205ZZE)
      const shown = token.group === 'seal' && /^2?N/.test(code) ? `-${code}` : code;
      segments.push({ code: shown, label, kind: token.group });
      rest = rest.slice(code.length);
    }
    flushUnknown();
    return { key: t.key, family, bore, segments, notes: t.notes };
  }

  /**
   * @param {string|null} family
   * @param {string} number spherical roller thrust bearings (29xxx) read the spherical table
   */
  function suffixTableFor(family, number = '') {
    if (family === 'deep-groove' || family === 'self-aligning') return 'ball';
    if (family === 'thrust' && /^29/.test(number)) return 'spherical';
    if (family === 'thrust') return 'ball';
    if (family === 'angular' || family === 'precision') return 'angular';
    return family;
  }

  function pushSeries(segments, series, family, hasPrefix) {
    if (!series) return;
    const typeNames = {
      'deep-groove': 'Deep groove ball bearing',
      angular: 'Angular contact ball bearing',
      precision: 'Angular contact ball bearing',
      'self-aligning': 'Self-aligning ball bearing',
      spherical: 'Spherical roller bearing',
      tapered: 'Tapered roller bearing',
      thrust: 'Thrust bearing',
    };
    if (hasPrefix || series.length > 2) {
      const name = hasPrefix ? 'Dimension series' : typeNames[family] || 'Bearing series';
      segments.push({ code: series, label: hasPrefix ? `${name} ${series}` : `${name}, dimension series ${series.slice(1)}`, kind: hasPrefix ? 'series' : 'type' });
      return;
    }
    if (series.length === 1) {
      segments.push({ code: series, label: typeNames[family] || 'Bearing type', kind: 'type' });
      return;
    }
    let typeLabel = typeNames[family] || 'Bearing type';
    if (/^[35][23]$/.test(series)) typeLabel = 'Double-row angular contact ball bearing';
    if (/^4[23]$/.test(series)) typeLabel = 'Double-row deep groove ball bearing';
    if (family === 'self-aligning' && /^2[23]$/.test(series)) typeLabel = 'Self-aligning ball bearing, wide width series';
    segments.push({ code: series[0], label: typeLabel, kind: 'type' });
    segments.push({ code: series[1], label: DIAMETER_SERIES[series[1]] || `Dimension series ${series[1]}`, kind: 'series' });
  }

  /** @param {number} inches */
  function formatInches(inches) {
    const whole = Math.floor(inches);
    const sixteenths = Math.round((inches - whole) * 16);
    if (!sixteenths) return `${whole}"`;
    let num = sixteenths;
    let den = 16;
    while (num % 2 === 0) {
      num /= 2;
      den /= 2;
    }
    return whole ? `${whole}-${num}/${den}"` : `${num}/${den}"`;
  }

  /**
   * Reads three numbers typed into the part number box, e.g. "25x52x15" or "25 52 15 mm".
   * @param {string} raw
   */
  function parseSizeQuery(raw) {
    const m = String(raw || '')
      .trim()
      .match(/^(\d+(?:\.\d+)?)\s*(?:[x×*]|\s)\s*(\d+(?:\.\d+)?)(?:\s*(?:[x×*]|\s)\s*(\d+(?:\.\d+)?))?\s*(mm|in|inch|")?$/i);
    if (!m) return null;
    const unit = m[4] && !/mm/i.test(m[4]) ? 'in' : 'mm';
    const [d, D, B] = [m[1], m[2], m[3]].map((v) => (v ? Number(v) : null));
    if (D && d >= D) return null;
    return { d, D, B, unit };
  }

  /* ------------------------------------------------------------------ */
  /* Index and search                                                    */
  /* ------------------------------------------------------------------ */

  /**
   * @typedef {object} Bearing
   * @property {string} pn
   * @property {string} key
   * @property {string} base
   * @property {string} suffix
   * @property {string} family
   * @property {number} d
   * @property {number} D
   * @property {number} B
   * @property {string} tail
   * @property {string} handle
   * @property {number} price
   * @property {number} compare
   * @property {number} variant
   * @property {string} image
   * @property {boolean} available
   * @property {boolean} matchedSet
   * @property {string|null} seal
   * @property {string} clearance
   */

  /**
   * Expands the compact JSON index into objects.
   * @param {any} raw
   * @returns {{ items: Bearing[], families: Array<[string, string]>, generated: string }}
   */
  function hydrateIndex(raw) {
    const items = [];
    for (const row of raw.items) {
      const [pn, alt, familyIdx, d, D, B, tailIdx, handle, price, compare, variant, imageIdx, flags] = row;
      const family = raw.families[familyIdx][0];
      const t = translate(pn);
      const tail = raw.tails[tailIdx] || '';
      items.push({
        pn,
        key: t.key,
        altKey: alt ? translate(alt).key : '',
        base: t.base,
        suffix: t.suffix,
        alt,
        family,
        d,
        D,
        B,
        tail,
        handle,
        price,
        compare,
        variant,
        image: imageIdx >= 0 ? raw.imageBase + raw.images[imageIdx] : '',
        available: Boolean(flags & 1),
        matchedSet: Boolean(flags & 2),
        seal: sealOf(family, t.suffix),
        clearance: clearanceOf(t.suffix, tail),
      });
    }
    return { items, families: raw.families, generated: raw.generated };
  }

  const SEALED_FAMILIES = new Set(['deep-groove', 'self-aligning', 'angular']);

  function sealOf(family, suffix) {
    if (!SEALED_FAMILIES.has(family)) return null;
    if (/NS/.test(suffix)) return 'contact';
    if (/NK/.test(suffix)) return 'noncontact';
    if (/Z/.test(suffix)) return 'shield';
    return 'open';
  }

  /**
   * @param {string} suffix
   * @param {string} tail title text after the dimensions, e.g. "Tapered 1:12 Bore, C3"
   */
  function clearanceOf(suffix, tail) {
    const fromSuffix = suffix.match(/C[2-5](?!\d)|CM/);
    if (fromSuffix) return fromSuffix[0];
    const fromTail = tail.match(/\b(C[2-5]|CM)\b/);
    return fromTail ? fromTail[1] : 'Normal';
  }

  const TAPER_FAMILIES = new Set(['spherical', 'self-aligning', 'cylindrical']);

  /**
   * Scores every bearing against a part number query.
   * @param {Bearing[]} items
   * @param {string} raw
   */
  function searchPartNumber(items, raw) {
    const q = translate(raw);
    if (!q.key) return { results: [], query: q };
    const qSuffix = canonical(q.suffix);
    // Compare seal type rather than spelling, so 6205-2RS1 ranks Nachi's 6205-2NSE9 first.
    const querySeal = /^(2?N[SK]|Z)/.test(qSuffix) ? sealOf(q.family || 'deep-groove', qSuffix) : null;
    const clearance = (q.suffix.match(/C[2-5]|CM/) || [])[0] || '';
    const queryTaper = TAPER_FAMILIES.has(q.family) ? /K/.test(q.suffix) : null;
    const scored = [];
    for (const item of items) {
      let score = 0;
      if (item.key === q.key) score = 1000;
      else if (item.altKey && item.altKey === q.key) score = 900;
      else if (item.key.startsWith(q.key)) score = 800 - Math.min(item.key.length - q.key.length, 50);
      else if (q.base && item.base === q.base) {
        score = 500;
        const itemSuffix = canonical(item.suffix);
        if (querySeal && item.seal === querySeal) score += 120;
        else if (querySeal) score -= 60;
        if (clearance && item.suffix.includes(clearance)) score += 80;
        if (queryTaper !== null) score += /K/.test(item.suffix) === queryTaper ? 60 : -80;
        score += 10 * Math.min(sharedPrefix(qSuffix, itemSuffix), 6);
      } else if (q.key.length >= 3 && item.key.includes(q.key)) score = 300;
      if (!score) continue;
      if (item.available) score += 5;
      scored.push({ item, score });
    }
    scored.sort((a, b) => b.score - a.score || naturalCompare(a.item.key, b.item.key));
    return { results: scored.map((s) => s.item), query: q, exact: scored[0]?.score >= 900 ? scored[0].item : null };
  }

  function sharedPrefix(a, b) {
    let n = 0;
    while (n < a.length && n < b.length && a[n] === b[n]) n++;
    return n;
  }

  /**
   * Finds bearings by size. Inch values are converted to millimetres.
   * @param {Bearing[]} items
   * @param {{d: number|null, D: number|null, B: number|null, unit: string}} size
   */
  function searchSize(items, size) {
    const factor = size.unit === 'in' ? 25.4 : 1;
    const wanted = ['d', 'D', 'B'].map((k) => (size[k] ? size[k] * factor : null));
    const tolerance = size.unit === 'in' ? 0.15 : 0.05;
    const exact = [];
    const near = [];
    for (const item of items) {
      let deviation = 0;
      let compared = 0;
      ['d', 'D', 'B'].forEach((k, i) => {
        if (wanted[i] == null) return;
        if (!item[k]) {
          deviation += 1e6;
          return;
        }
        deviation += Math.abs(item[k] - wanted[i]);
        compared++;
      });
      if (!compared || deviation >= 1e6) continue;
      if (deviation <= tolerance * compared) exact.push({ item, deviation });
      else if (deviation <= 6 * compared) near.push({ item, deviation });
    }
    const sorter = (a, b) =>
      a.deviation - b.deviation ||
      familyRank(a.item.family) - familyRank(b.item.family) ||
      Number(b.item.available) - Number(a.item.available) ||
      naturalCompare(a.item.key, b.item.key);
    exact.sort(sorter);
    near.sort(sorter);
    return { exact: exact.map((e) => e.item), near: near.slice(0, 48).map((e) => e.item) };
  }

  const collator = new Intl.Collator('en', { numeric: true });
  const naturalCompare = (a, b) => collator.compare(a, b);

  /** Order for bearings that share a size: the most commonly replaced families first. */
  const FAMILY_ORDER = ['deep-groove', 'cylindrical', 'spherical', 'tapered', 'angular', 'self-aligning', 'precision', 'thrust', 'other'];
  const familyRank = (family) => {
    const i = FAMILY_ORDER.indexOf(family);
    return i < 0 ? FAMILY_ORDER.length : i;
  };

  const core = { translate, canonical, decode, parseSizeQuery, hydrateIndex, searchPartNumber, searchSize, boreFromCode, guessFamily, sealOf, clearanceOf, formatInches };
  globalThis.NachiFinderCore = core;

  if (typeof customElements === 'undefined' || typeof HTMLElement === 'undefined') return;

  /* ------------------------------------------------------------------ */
  /* Hero finder                                                         */
  /* ------------------------------------------------------------------ */

  /** Short row labels under the part number (uppercased by CSS), by segment kind. */
  const ROW_LABELS = {
    type: 'type',
    series: 'series',
    bore: 'bore',
    size: 'size',
    seal: 'seals',
    ring: 'snap ring',
    taper: 'taper',
    clearance: 'clearance',
    cage: 'cage',
    angle: 'angle',
    arrangement: 'pairing',
    preload: 'preload',
    precision: 'precision',
    design: 'design',
    lube: 'lube',
    vibration: 'duty',
    other: 'other',
  };

  const SEAL_LABELS = { open: 'Open', shield: 'Shielded (ZZE, ZE)', contact: 'Contact seals (2NSE)', noncontact: 'Non-contact seals (2NKE)' };
  const productCache = new Map();
  let measureCanvas = null;

  /**
   * Wraps the hero's card and the results under it. The card's rows (hover, tap, keyboard) stay with the hero's
   * own script; this redraws them for the typed number and asks the hero to bind them again.
   */
  class NachiFinder extends HTMLElement {
    /** @type {{ items: Bearing[], families: Array<[string,string]> } | null} */
    #index = null;
    #indexPromise = null;
    #results = [];
    #shown = 0;
    #filters = { family: '', seal: '', clearance: '', stock: false };
    #sort = 'relevance';
    #lastQuery = null;
    #hydrateController = null;
    #typingTimer = 0;
    #renderedKey = '';

    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.pageSize = Number(this.dataset.pageSize) || 24;
      this.money = new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.usesStoreCurrency = (this.dataset.currency || 'USD') === (this.dataset.shopCurrency || 'USD');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.hero = this.closest('nachi-hero');
      this.card = this.querySelector('.nachi-hero__title-block');
      this.badge = this.querySelector('[data-nachi-card-badge]');
      this.partLabel = this.querySelector('[data-nachi-part-label]');
      this.partEl = this.querySelector('.nachi-hero__part');
      this.dimsEl = this.querySelector('.nachi-hero__dims');
      this.detailsEl = this.querySelector('.nachi-hero__details');
      this.pnForm = this.querySelector('[data-nachi-form="pn"]');
      this.sizeForm = this.querySelector('[data-nachi-form="size"]');
      this.pnInput = this.querySelector('[data-nachi-input="pn"]');
      this.resultsEl = this.querySelector('[data-nachi-results]');
      this.statusEl = this.querySelector('[data-nachi-status]');
      this.noteEl = this.querySelector('[data-nachi-note]');
      this.listEl = this.querySelector('[data-nachi-list]');
      this.moreButton = this.querySelector('[data-nachi-more]');
      this.filtersEl = this.querySelector('[data-nachi-filters]');
      this.emptyEl = this.querySelector('[data-nachi-empty]');
      this.rowTemplate = this.querySelector('[data-nachi-row-template]');
      this.toast = this.querySelector('[data-nachi-toast]');

      this.pnForm?.addEventListener('submit', (event) => this.#onPartSubmit(event), { signal });
      this.pnInput?.addEventListener('input', () => this.#onPartInput(), { signal });
      this.sizeForm?.addEventListener('submit', (event) => this.#onSizeSubmit(event), { signal });
      this.querySelector('[data-nachi-modes]')?.addEventListener('click', (event) => this.#onModeClick(event), { signal });
      this.filtersEl?.addEventListener('click', (event) => this.#onFilterClick(event), { signal });
      this.filtersEl?.addEventListener('change', (event) => this.#onFilterChange(event), { signal });
      this.moreButton?.addEventListener('click', () => this.#renderPage(false), { signal });
      this.listEl?.addEventListener('click', (event) => this.#onAddToCart(event), { signal });
      document.addEventListener('click', (event) => this.#onPageLinkClick(event), { signal });
      for (const el of [this.pnInput, this.sizeForm]) el?.addEventListener('focusin', () => this.#loadIndex(), { signal, once: true });

      // The authored example (section blocks) comes back whenever the box is emptied.
      this.initial = {
        part: this.partEl?.innerHTML || '',
        dims: this.dimsEl?.innerHTML || '',
        details: this.detailsEl?.innerHTML || '',
        count: this.card?.style.getPropertyValue('--nachi-seg-count') || '1',
        fit: this.card?.style.getPropertyValue('--nachi-part-fit') || '',
        label: this.partLabel?.textContent || '',
      };

      const params = new URL(window.location.href).searchParams;
      if (params.has('pn') || params.has('d') || params.has('D') || params.has('type')) {
        this.#restoreFromUrl(params);
      } else if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this.#loadIndex(), { timeout: 4000 });
      } else {
        setTimeout(() => this.#loadIndex(), 2500);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.#hydrateController?.abort();
      clearTimeout(this.#typingTimer);
    }

    /* ---------- Data ---------- */

    #loadIndex() {
      if (this.#indexPromise) return this.#indexPromise;
      this.#indexPromise = (async () => {
        try {
          const response = await fetch(this.dataset.indexUrl, { priority: 'low' });
          if (!response.ok) throw new Error(`Index HTTP ${response.status}`);
          this.#index = hydrateIndex(await response.json());
          this.#renderFamilyCounts();
        } catch (error) {
          console.warn('[Nachi finder] index unavailable, using store search instead', error);
          this.#index = null;
        }
        return this.#index;
      })();
      return this.#indexPromise;
    }

    #renderFamilyCounts() {
      const counts = {};
      for (const item of this.#index.items) counts[item.family] = (counts[item.family] || 0) + 1;
      for (const el of document.querySelectorAll('[data-nachi-family-count]')) {
        const n = counts[el.dataset.nachiFamilyCount];
        if (n) el.textContent = n.toLocaleString('en-US');
      }
    }

    /* ---------- Modes ---------- */

    #onModeClick(event) {
      const button = event.target.closest('[data-nachi-mode]');
      if (button) this.selectMode(button.dataset.nachiMode);
    }

    /** @param {string} mode pn | size | type */
    selectMode(mode) {
      for (const button of this.querySelectorAll('[data-nachi-mode]')) button.setAttribute('aria-pressed', String(button.dataset.nachiMode === mode));
      for (const panel of this.querySelectorAll('[data-nachi-panel]')) panel.hidden = panel.dataset.nachiPanel !== mode;
      this.#loadIndex();
    }

    /* ---------- Part number ---------- */

    #onPartInput() {
      const value = this.pnInput.value;
      this.#renderDecode(value);
      clearTimeout(this.#typingTimer);
      if (value.trim().length < 2) return;
      this.#typingTimer = setTimeout(() => this.searchPart(value, { live: true }), 260);
    }

    #onPartSubmit(event) {
      event.preventDefault();
      const value = this.pnInput.value.trim();
      if (!value) {
        this.pnInput.focus();
        return;
      }
      clearTimeout(this.#typingTimer);
      this.searchPart(value);
    }

    /**
     * @param {string} value
     * @param {{ live?: boolean }} [options] live searches (while typing) never scroll the page
     */
    async searchPart(value, options = {}) {
      const size = parseSizeQuery(value);
      if (size) {
        this.#fillSizeForm(size);
        return this.searchBySize(size, options);
      }
      const index = await this.#loadIndex();
      if (!index) return this.#fallbackSearch(value);

      const { results, query, exact } = searchPartNumber(index.items, value);
      this.#lastQuery = { type: 'pn', value, query };
      this.#results = results;
      this.#resetFilters();
      this.#renderDecode(value, exact);
      const notes = query.notes.map(([from, to]) => `${from} is ${to} in Nachi numbering`);
      this.#renderResults({
        status: results.length
          ? `${pluralize(results.length, 'Nachi bearing')} ${results.length === 1 ? 'matches' : 'match'} ${value.trim().toUpperCase()}`
          : `No Nachi bearing matches ${value.trim().toUpperCase()}`,
        note: notes.length ? `Showing Nachi equivalents. ${notes.join('. ')}.` : '',
      });
      this.#updateUrl({ pn: value.trim() });
      this.#track('nachi_finder_search', { mode: 'part_number', query: value.trim(), results: results.length });
      if (!options.live) this.#revealResults();
    }

    /**
     * Redraws the segmented part number, its dimension lines and readings for what was typed.
     * An empty box brings back the server-rendered example.
     * @param {string} value
     * @param {Bearing|null} [exact]
     */
    #renderDecode(value, exact = null) {
      const trimmed = value.trim();
      if (!trimmed || parseSizeQuery(trimmed)) {
        this.#restoreExample();
        return;
      }
      const result = decode(trimmed, exact?.family);
      if (!result.segments.length || !this.card || !this.partEl || !this.dimsEl || !this.detailsEl) return;
      const key = result.segments.map((s) => `${s.code}:${s.kind}`).join('|');
      if (this.badge) this.badge.textContent = this.dataset.yoursLabel || 'Your part number';
      if (key === this.#renderedKey) return;
      this.#renderedKey = key;

      const uid = this.dataset.uid || 'nachi';
      const segments = result.segments;
      // a typographic space before the clearance, as Nachi prints it (6205-2NSE9 C3)
      const spaced = (i) => i > 0 && i < segments.length - 1 && segments[i + 1].kind === 'clearance';
      this.card.style.setProperty('--nachi-seg-count', String(segments.length));
      this.card.style.removeProperty('--nachi-part-fit');
      this.card.classList.add('is-live');

      const part = segments.map((segment, i) => {
        const span = document.createElement('span');
        span.className = `nachi-hero__seg${spaced(i) ? ' nachi-hero__seg--space' : ''}`;
        span.dataset.nachiSeg = String(i + 1);
        span.style.setProperty('--nachi-i', String(i + 1));
        span.textContent = segment.code;
        return span;
      });
      this.partEl.replaceChildren(...part);
      if (this.partLabel) this.partLabel.textContent = `${this.dataset.yoursLabel || 'Your part number'} ${segments.map((s) => s.code).join('')}`;

      const rows = segments.map((segment, i) => {
        const li = document.createElement('li');
        li.className = 'nachi-hero__dim';
        li.dataset.nachiDim = String(i + 1);
        li.style.setProperty('--nachi-i', String(i + 1));
        li.innerHTML =
          '<svg class="nachi-hero__dimline" viewBox="0 0 100 16" preserveAspectRatio="none" aria-hidden="true" focusable="false"><path d="M0 2 V14 M0 8 H100 M100 2 V14" vector-effect="non-scaling-stroke"/></svg>' +
          '<div class="nachi-hero__dimtext"><span class="nachi-hero__dimghost" aria-hidden="true"></span><span class="nachi-hero__dimlabel"></span></div>';
        const ghost = li.querySelector('.nachi-hero__dimghost');
        ghost.textContent = segment.code;
        if (spaced(i)) ghost.classList.add('nachi-hero__seg--space');
        const label = li.querySelector('.nachi-hero__dimlabel');
        label.id = `${uid}-dimlabel-${i + 1}`;
        label.textContent = ROW_LABELS[segment.kind] || 'code';
        return li;
      });
      this.dimsEl.replaceChildren(...rows);

      const details = segments.map((segment, i) => {
        const div = document.createElement('div');
        div.className = 'nachi-hero__dimdetail';
        div.id = `${uid}-dim-${i + 1}`;
        div.dataset.nachiDetail = String(i + 1);
        div.textContent = detailFor(segment);
        return div;
      });
      this.detailsEl.replaceChildren(...details);

      this.hero?.refreshTitleBlock?.();
      this.#fitPart();
    }

    #restoreExample() {
      if (!this.#renderedKey || !this.initial) return;
      this.#renderedKey = '';
      this.partEl.innerHTML = this.initial.part;
      this.dimsEl.innerHTML = this.initial.dims;
      this.detailsEl.innerHTML = this.initial.details;
      this.card.style.setProperty('--nachi-seg-count', this.initial.count);
      if (this.initial.fit) this.card.style.setProperty('--nachi-part-fit', this.initial.fit);
      else this.card.style.removeProperty('--nachi-part-fit');
      this.card.classList.remove('is-live');
      if (this.partLabel) this.partLabel.textContent = this.initial.label;
      if (this.badge) this.badge.textContent = this.dataset.exampleLabel || 'Example part number';
      this.hero?.refreshTitleBlock?.();
    }

    /**
     * Shrinks the part number until every row's label fits to the right of its line, like the card's
     * Liquid-computed --nachi-part-fit, but measured because the part number changes as the visitor types.
     */
    #fitPart() {
      requestAnimationFrame(() => {
        const segs = [...this.partEl.children];
        if (!segs.length || !this.card.isConnected) return;
        const cardStyle = getComputedStyle(this.card);
        const inner = this.card.clientWidth - parseFloat(cardStyle.paddingLeft) - parseFloat(cardStyle.paddingRight);
        const fontSize = parseFloat(getComputedStyle(segs[0]).fontSize);
        const left = this.partEl.getBoundingClientRect().left;
        const labels = [...this.dimsEl.querySelectorAll('.nachi-hero__dimlabel')];
        let scale = 1;
        segs.forEach((seg, i) => {
          const end = seg.getBoundingClientRect().right - left;
          const room = inner - 14 - longestWordWidth(labels[i]);
          if (end > room && end > 0) scale = Math.min(scale, room / end);
        });
        if (scale < 1) this.card.style.setProperty('--nachi-part-fit', `${Math.max(16, Math.floor(fontSize * scale))}px`);
      });
    }

    /* ---------- Size ---------- */

    #onSizeSubmit(event) {
      event.preventDefault();
      const size = this.#readSizeForm();
      const error = this.querySelector('[data-nachi-size-error]');
      const filled = [size.d, size.D, size.B].filter(Boolean).length;
      let message = '';
      if (filled < 1) message = 'Enter at least the bore or the outside diameter.';
      else if (size.d && size.D && size.d >= size.D) message = 'The bore must be smaller than the outside diameter.';
      if (error) {
        error.textContent = message;
        error.hidden = !message;
      }
      if (message) return;
      this.searchBySize(size);
    }

    #readSizeForm() {
      const data = new FormData(this.sizeForm);
      const num = (name) => {
        const v = Number(String(data.get(name) || '').replace(',', '.'));
        return Number.isFinite(v) && v > 0 ? v : null;
      };
      return { d: num('d'), D: num('D'), B: num('B'), unit: data.get('unit') === 'in' ? 'in' : 'mm' };
    }

    #fillSizeForm(size) {
      if (!this.sizeForm) return;
      for (const k of ['d', 'D', 'B']) {
        const input = this.sizeForm.querySelector(`[name="${k}"]`);
        if (input) input.value = size[k] ?? '';
      }
      const unit = this.sizeForm.querySelector(`[name="unit"][value="${size.unit}"]`);
      if (unit) unit.checked = true;
    }

    /**
     * @param {{ d: number|null, D: number|null, B: number|null, unit: string }} size
     * @param {{ live?: boolean }} [options]
     */
    async searchBySize(size, options = {}) {
      const index = await this.#loadIndex();
      if (!index) return this.#fallbackSearch([size.d, size.D, size.B].filter(Boolean).join(' x '));
      const { exact, near } = searchSize(index.items, size);
      const label = sizeLabel(size);
      this.#lastQuery = { type: 'size', size };
      this.#results = exact.length ? exact : near;
      this.#resetFilters();
      this.#renderResults({
        status: exact.length
          ? `${pluralize(exact.length, 'Nachi bearing')} ${exact.length === 1 ? 'is' : 'are'} ${label}`
          : near.length
            ? `No Nachi bearing is exactly ${label}`
            : `No Nachi bearing is close to ${label}`,
        note: !exact.length && near.length ? 'These are the closest sizes we stock. Check each dimension before you order.' : '',
      });
      const params = { d: size.d, D: size.D, B: size.B };
      if (size.unit === 'in') params.unit = 'in';
      this.#updateUrl(params);
      this.#track('nachi_finder_search', { mode: 'size', query: label, results: this.#results.length });
      if (!options.live) this.#revealResults();
    }

    /* ---------- Type ---------- */

    /** @param {string} family */
    async browseFamily(family) {
      const index = await this.#loadIndex();
      const label = new Map(index?.families || []).get(family) || family;
      if (!index) return this.#fallbackSearch(label);
      this.#lastQuery = { type: 'family', family };
      this.#results = index.items.filter((item) => item.family === family).sort((a, b) => (a.d || 1e9) - (b.d || 1e9) || naturalCompare(a.key, b.key));
      this.#resetFilters();
      this.#renderResults({ status: `${pluralize(this.#results.length, 'Nachi bearing')} in ${label.toLowerCase()}`, note: 'Sorted by bore, smallest first.' });
      for (const button of this.querySelectorAll('[data-nachi-family]')) {
        button.setAttribute('aria-pressed', String(button.dataset.nachiFamily === family));
      }
      this.#updateUrl({ type: family });
      this.#track('nachi_finder_search', { mode: 'type', query: family, results: this.#results.length });
      this.#revealResults();
    }

    /* ---------- Links from anywhere on the page ---------- */

    #onPageLinkClick(event) {
      const link = event.target.closest('[data-nachi-query], [data-nachi-family]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
      event.preventDefault();
      if (link.dataset.nachiFamily) {
        this.selectMode('type');
        this.browseFamily(link.dataset.nachiFamily);
        return;
      }
      this.selectMode('pn');
      this.pnInput.value = link.dataset.nachiQuery;
      this.#renderDecode(link.dataset.nachiQuery);
      this.searchPart(link.dataset.nachiQuery);
    }

    #restoreFromUrl(params) {
      if (params.has('type')) {
        this.selectMode('type');
        this.browseFamily(params.get('type'));
        return;
      }
      if (params.has('pn')) {
        this.pnInput.value = params.get('pn');
        this.#renderDecode(params.get('pn'));
        this.searchPart(params.get('pn'), { live: true });
        return;
      }
      const size = {
        d: Number(params.get('d')) || null,
        D: Number(params.get('D')) || null,
        B: Number(params.get('B')) || null,
        unit: params.get('unit') === 'in' ? 'in' : 'mm',
      };
      this.selectMode('size');
      this.#fillSizeForm(size);
      this.searchBySize(size, { live: true });
    }

    #updateUrl(params) {
      const url = new URL(window.location.href);
      for (const key of ['pn', 'd', 'D', 'B', 'unit', 'type']) url.searchParams.delete(key);
      for (const [key, value] of Object.entries(params)) if (value) url.searchParams.set(key, String(value));
      url.hash = '';
      history.replaceState(history.state, '', url);
    }

    /* ---------- Results ---------- */

    #resetFilters() {
      this.#filters = { family: '', seal: '', clearance: '', stock: false };
      this.#sort = 'relevance';
    }

    #filtered() {
      const f = this.#filters;
      const list = this.#results.filter(
        (item) =>
          (!f.family || item.family === f.family) &&
          (!f.seal || item.seal === f.seal) &&
          (!f.clearance || item.clearance === f.clearance) &&
          (!f.stock || item.available),
      );
      if (this.#sort === 'price-asc') return [...list].sort((a, b) => a.price - b.price);
      if (this.#sort === 'price-desc') return [...list].sort((a, b) => b.price - a.price);
      if (this.#sort === 'size') return [...list].sort((a, b) => (a.d || 1e9) - (b.d || 1e9) || (a.D || 0) - (b.D || 0));
      return list;
    }

    #renderResults({ status, note }) {
      this.resultsEl.hidden = false;
      this.statusEl.textContent = status;
      this.noteEl.textContent = note;
      this.noteEl.hidden = !note;
      this.#renderFilters();
      this.#renderPage(true);
    }

    #renderFilters() {
      if (!this.filtersEl) return;
      const families = new Map(this.#index?.families || []);
      const groups = [
        { name: 'family', label: 'Type', values: countBy(this.#results, 'family'), text: (v) => families.get(v) || v },
        { name: 'seal', label: 'Seals', values: countBy(this.#results, 'seal'), text: (v) => SEAL_LABELS[v] || v },
        { name: 'clearance', label: 'Clearance', values: countBy(this.#results, 'clearance'), text: (v) => (v === 'CM' ? 'Electric motor (CM)' : v) },
      ];
      const fragment = document.createDocumentFragment();
      for (const group of groups) {
        const entries = [...group.values.entries()].filter(([value]) => value != null);
        if (entries.length < 2) continue;
        entries.sort((a, b) => b[1] - a[1]);
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'nachi-results__group';
        const legend = document.createElement('legend');
        legend.className = 'nachi-readout nachi-results__legend';
        legend.textContent = group.label;
        const chips = document.createElement('div');
        chips.className = 'nachi-chips';
        for (const [value, count] of [['', this.#results.length], ...entries]) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'nachi-chip';
          button.dataset.filter = group.name;
          button.dataset.value = value;
          button.setAttribute('aria-pressed', String(this.#filters[group.name] === value));
          button.textContent = value ? group.text(value) : 'All';
          const countEl = document.createElement('span');
          countEl.className = 'nachi-chip__count';
          countEl.textContent = count.toLocaleString('en-US');
          button.append(countEl);
          chips.append(button);
        }
        fieldset.append(legend, chips);
        fragment.append(fieldset);
      }

      const tools = document.createElement('div');
      tools.className = 'nachi-results__tools';
      tools.innerHTML = `
        <button type="button" class="nachi-chip" data-filter-stock aria-pressed="${this.#filters.stock}">In stock only</button>
        <label class="nachi-results__sort"><span class="nachi-readout">Sort</span>
          <select class="nachi-field__input nachi-results__select" data-sort>
            <option value="relevance">Best match</option>
            <option value="size">Bore, smallest first</option>
            <option value="price-asc">Price, low to high</option>
            <option value="price-desc">Price, high to low</option>
          </select>
        </label>`;
      tools.querySelector('select').value = this.#sort;
      fragment.append(tools);
      this.filtersEl.replaceChildren(fragment);
    }

    #onFilterClick(event) {
      const stock = event.target.closest('[data-filter-stock]');
      if (stock) {
        this.#filters.stock = !this.#filters.stock;
        stock.setAttribute('aria-pressed', String(this.#filters.stock));
        this.#renderPage(true);
        return;
      }
      const button = event.target.closest('[data-filter]');
      if (!button) return;
      this.#filters[button.dataset.filter] = button.dataset.value;
      for (const chip of this.filtersEl.querySelectorAll(`[data-filter="${button.dataset.filter}"]`)) {
        chip.setAttribute('aria-pressed', String(chip === button));
      }
      this.#renderPage(true);
    }

    #onFilterChange(event) {
      if (!event.target.matches('[data-sort]')) return;
      this.#sort = event.target.value;
      this.#renderPage(true);
    }

    /** @param {boolean} reset start from the first page */
    #renderPage(reset) {
      const list = this.#filtered();
      if (reset) {
        this.#shown = 0;
        this.listEl.replaceChildren();
      }
      const slice = list.slice(this.#shown, this.#shown + this.pageSize);
      const rows = slice.map((item) => this.#renderRow(item));
      this.listEl.append(...rows);
      this.#shown += slice.length;

      const remaining = list.length - this.#shown;
      this.moreButton.hidden = remaining <= 0;
      if (remaining > 0) this.moreButton.textContent = `Show ${Math.min(remaining, this.pageSize)} more of ${remaining.toLocaleString('en-US')}`;
      this.listEl.closest('[data-nachi-panel-list]').hidden = list.length === 0;
      this.emptyEl.hidden = list.length > 0;
      this.#updateIndexLinks();
      if (!reset && rows[0]) rows[0].querySelector('.nachi-result__link')?.focus();
      this.#hydrate(slice, rows);
    }

    /** @param {Bearing} item */
    #renderRow(item) {
      const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
      const url = `/products/${item.handle}`;
      const familyLabel = new Map(this.#index?.families || []).get(item.family) || '';
      row.dataset.handle = item.handle;
      for (const link of row.querySelectorAll('[data-row-link]')) link.href = url;
      const img = row.querySelector('[data-row-image]');
      if (img && item.image) img.src = `${item.image}?width=160`;
      else img?.closest('[data-row-link]')?.remove();
      setText(row, '[data-row-pn]', `Nachi ${item.pn}`);
      setText(row, '[data-row-type]', familyLabel + (item.matchedSet ? ', matched pair' : ''));
      setText(row, '[data-row-bore]', item.d ? formatNumber(item.d) : '–');
      setText(row, '[data-row-od]', item.D ? formatNumber(item.D) : '–');
      setText(row, '[data-row-width]', item.B ? formatNumber(item.B) : '–');
      setText(row, '[data-row-detail]', describeDetail(item));
      this.#renderPrice(row, item.price, item.compare, item.available, Boolean(this.usesStoreCurrency));
      const button = row.querySelector('[data-row-add]');
      if (button) {
        button.dataset.variant = String(item.variant);
        button.setAttribute('aria-label', `Add Nachi ${item.pn} to cart`);
      }
      if (this.#lastQuery?.type === 'pn') highlight(row.querySelector('[data-row-pn]'), this.#lastQuery.query.base);
      return row;
    }

    #renderPrice(row, price, compare, available, show) {
      const priceEl = row.querySelector('[data-row-price]');
      const compareEl = row.querySelector('[data-row-compare]');
      const stockEl = row.querySelector('[data-row-stock]');
      const button = row.querySelector('[data-row-add]');
      if (priceEl) priceEl.textContent = show ? this.money.format(price / 100) : '';
      if (compareEl) {
        compareEl.hidden = !(show && compare > price);
        compareEl.textContent = compare > price ? this.money.format(compare / 100) : '';
      }
      if (stockEl) {
        stockEl.textContent = available ? 'In stock' : 'Sold out, ask for lead time';
        stockEl.dataset.available = String(available);
      }
      if (button) {
        button.disabled = !available;
        button.textContent = available ? 'Add to cart' : 'Sold out';
      }
    }

    /**
     * Refreshes price and stock from the storefront for the rows on screen.
     * @param {Bearing[]} items
     * @param {HTMLElement[]} rows
     */
    async #hydrate(items, rows) {
      this.#hydrateController?.abort();
      const controller = new AbortController();
      this.#hydrateController = controller;
      const queue = items.map((item, i) => ({ item, row: rows[i] }));
      const worker = async () => {
        while (queue.length && !controller.signal.aborted) {
          const { item, row } = queue.shift();
          try {
            const product = await fetchProduct(item.handle, controller.signal);
            if (!product) {
              row.dataset.unavailable = 'true';
              continue;
            }
            const variant = product.variants.find((v) => v.id === item.variant) || product.variants[0];
            item.price = variant.price;
            item.compare = variant.compare_at_price || 0;
            item.available = variant.available;
            item.variant = variant.id;
            this.#renderPrice(row, variant.price, variant.compare_at_price || 0, variant.available, true);
            row.querySelector('[data-row-add]')?.setAttribute('data-variant', String(variant.id));
          } catch (error) {
            if (error.name === 'AbortError') return;
          }
        }
      };
      await Promise.all(Array.from({ length: 6 }, worker));
    }

    #updateIndexLinks() {
      const value = this.#lastQuery?.type === 'pn' ? this.#lastQuery.value : '';
      for (const link of this.querySelectorAll('[data-nachi-index-link]')) {
        if (!link.dataset.base) link.dataset.base = link.getAttribute('href');
        const url = new URL(link.dataset.base, window.location.origin);
        if (value) url.searchParams.set('q', value);
        else url.searchParams.delete('q');
        link.href = url.pathname + url.search;
      }
    }

    #revealResults() {
      const top = this.resultsEl.getBoundingClientRect().top;
      if (top > window.innerHeight * 0.7 || top < 0) {
        this.resultsEl.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    }

    /** Uses Shopify's predictive search when the index cannot load. */
    async #fallbackSearch(value) {
      this.resultsEl.hidden = false;
      this.statusEl.textContent = 'Searching the store';
      this.noteEl.hidden = true;
      this.filtersEl?.replaceChildren();
      this.listEl.replaceChildren();
      this.moreButton.hidden = true;
      try {
        const url = new URL('/search/suggest.json', window.location.origin);
        url.searchParams.set('q', `Nachi ${value}`);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '10');
        const data = await (await fetch(url)).json();
        const products = data.resources?.results?.products || [];
        this.statusEl.textContent = products.length ? `${pluralize(products.length, 'result')} for ${value}` : `No results for ${value}`;
        for (const product of products) {
          const row = this.rowTemplate.content.firstElementChild.cloneNode(true);
          for (const link of row.querySelectorAll('[data-row-link]')) link.href = product.url;
          setText(row, '[data-row-pn]', product.title);
          const img = row.querySelector('[data-row-image]');
          if (img && product.image) img.src = product.image;
          row.querySelector('[data-row-add]')?.remove();
          row.querySelector('.nachi-result__dims')?.remove();
          setText(row, '[data-row-price]', product.price ? this.money.format(Number(product.price)) : '');
          this.listEl.append(row);
        }
        this.listEl.closest('[data-nachi-panel-list]').hidden = products.length === 0;
        this.emptyEl.hidden = products.length > 0;
      } catch {
        this.statusEl.textContent = 'Search is unavailable right now. Try the store search or contact us.';
      }
    }

    /* ---------- Cart ---------- */

    async #onAddToCart(event) {
      const button = event.target.closest('[data-row-add]');
      if (!button || button.disabled) return;
      const id = Number(button.dataset.variant);
      const pn = button.closest('[data-handle]')?.querySelector('[data-row-pn]')?.textContent || '';
      const label = button.textContent;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding';
      const drawer = document.querySelector('cart-drawer');
      try {
        const body = { id, quantity: 1 };
        if (drawer?.getSectionsToRender) {
          body.sections = drawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'nachi-finder', productVariantId: id, cartData: data });
        }
        if (drawer?.renderContents && data.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(data);
        } else {
          this.#showToast(`${pn} added to your cart.`, true);
        }
        this.#track('add_to_cart_nachi_finder', { item_id: pn });
        button.textContent = 'Added';
        setTimeout(() => {
          button.textContent = label;
          button.disabled = false;
        }, 1800);
      } catch (error) {
        this.#showToast(error.message || 'Could not add to cart. Try again from the product page.', false);
        button.textContent = label;
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    #showToast(message, withCartLink) {
      if (!this.toast) return;
      this.toast.querySelector('[data-nachi-toast-text]').textContent = message;
      const link = this.toast.querySelector('a');
      if (link) link.hidden = !withCartLink;
      this.toast.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => (this.toast.hidden = true), 6000);
    }

    #track(event, detail) {
      window.dataLayer?.push({ event, ...detail });
    }
  }

  /* ---------- Helpers ---------- */

  /** "05 × 5 = 25 mm bore" for bore codes, "2NSE9 = contact rubber seals on both sides" for the rest. */
  function detailFor(segment) {
    const code = segment.code.replace(/^-/, '');
    if (segment.kind === 'bore' && /^\d\d$/.test(code) && Number(code) >= 4 && /^\d+ mm bore$/.test(segment.label)) {
      return `${code} × 5 = ${segment.label}`;
    }
    const label = /^[A-Z][a-z]/.test(segment.label) ? segment.label[0].toLowerCase() + segment.label.slice(1) : segment.label;
    return `${code} = ${label}`;
  }

  /** Width of the longest word in a dimension label, at the label's own type settings. */
  function longestWordWidth(label) {
    if (!label) return 0;
    measureCanvas ||= document.createElement('canvas').getContext('2d');
    const style = getComputedStyle(label);
    measureCanvas.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const spacing = parseFloat(style.letterSpacing) || 0;
    let widest = 0;
    for (const word of label.textContent.toUpperCase().split(/\s+/)) {
      widest = Math.max(widest, measureCanvas.measureText(word).width + spacing * word.length);
    }
    return widest;
  }

  const formatNumber = (n) => String(Number(Number(n).toFixed(3)));

  async function fetchProduct(handle, signal) {
    if (productCache.has(handle)) return productCache.get(handle);
    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, { signal });
    const product = response.ok ? await response.json() : null;
    productCache.set(handle, product);
    return product;
  }

  function countBy(items, key) {
    const map = new Map();
    for (const item of items) map.set(item[key], (map.get(item[key]) || 0) + 1);
    return map;
  }

  function describeDetail(item) {
    const parts = [];
    if (/Tapered 1:(12|30)/.test(item.tail)) parts.push(`Tapered bore ${item.tail.match(/1:\d+/)[0]}`);
    const seal = { shield: 'Steel shields', contact: 'Contact seals', noncontact: 'Non-contact seals', open: 'Open' }[item.seal];
    if (seal) parts.push(seal);
    if (item.clearance === 'Normal') parts.push('Normal clearance');
    else if (item.clearance === 'CM') parts.push('Electric motor clearance');
    else parts.push(`${item.clearance} clearance`);
    return parts.join(', ');
  }

  function sizeLabel(size) {
    const unit = size.unit === 'in' ? '"' : ' mm';
    const parts = [];
    if (size.d) parts.push(`${formatNumber(size.d)}${unit} bore`);
    if (size.D) parts.push(`${formatNumber(size.D)}${unit} OD`);
    if (size.B) parts.push(`${formatNumber(size.B)}${unit} wide`);
    return parts.join(', ');
  }

  function pluralize(n, word) {
    return `${n.toLocaleString('en-US')} ${word}${n === 1 ? '' : 's'}`;
  }

  function setText(root, selector, text) {
    const el = root.querySelector(selector);
    if (el) el.textContent = text;
  }

  /** Underlines the basic number in the result's part number when it is the one that was typed. */
  function highlight(el, base) {
    if (!el || !base) return;
    const text = el.textContent;
    const start = text.startsWith('Nachi ') ? 6 : 0;
    let k = 0;
    let end = -1;
    for (let i = start; i < text.length && k < base.length; i++) {
      if (/[\s-]/.test(text[i])) continue;
      if (text[i].toUpperCase() !== base[k]) break;
      k++;
      end = i;
    }
    if (k < Math.min(base.length, 2) || end < start) return;
    const mark = document.createElement('mark');
    mark.className = 'nachi-result__match';
    mark.textContent = text.slice(start, end + 1);
    el.replaceChildren(text.slice(0, start), mark, text.slice(end + 1));
  }

  if (!customElements.get('nachi-finder')) customElements.define('nachi-finder', NachiFinder);
})();
